/**
 * Mermaid 流程图插入对话框
 * 支持 AI 自动生成 + 手动编辑
 */
import React, { useState, useEffect, useRef } from 'react';
import mermaid from 'mermaid';

// 全局标记，确保 Mermaid 只初始化一次
let mermaidDialogInitialized = false;
let mermaidDialogInitTimer: NodeJS.Timeout | null = null;

interface MermaidDialogProps {
  show: boolean;
  onClose: () => void;
  onInsert: (code: string) => void;
  initialCode?: string;
  contextText?: string; // 光标前的文字，用于 AI 生成
  selectedModel?: string; // AI模型名称
}

const MermaidDialog: React.FC<MermaidDialogProps> = ({
  show,
  onClose,
  onInsert,
  initialCode = '',
  contextText = '',
  selectedModel = 'gpt-4o-mini', // 默认模型
}) => {
  const [code, setCode] = useState(initialCode);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string>('');
  const [scale, setScale] = useState(1); // 缩放比例
  const [position, setPosition] = useState({ x: 0, y: 0 }); // 拖动位置
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖动
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }); // 拖动起始位置
  const previewRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // 初始化 Mermaid
  useEffect(() => {
    // 如果已经初始化或正在初始化，直接返回
    if (mermaidDialogInitialized || mermaidDialogInitTimer) {
      return;
    }
    
    // 立即初始化 Mermaid（警告已在 index.tsx 中屏蔽）
    mermaidDialogInitTimer = setTimeout(() => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });
      mermaidDialogInitialized = true;
      mermaidDialogInitTimer = null;
    }, 0);
    
    return () => {
      if (mermaidDialogInitTimer) {
        clearTimeout(mermaidDialogInitTimer);
        mermaidDialogInitTimer = null;
      }
    };
  }, []);

  // 鼠标滚轮缩放
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation(); // 阻止事件冒泡，防止下层滚动
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  // 鼠标按下开始拖动
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  // 鼠标移动进行拖动
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  // 鼠标松开停止拖动
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 重置视图
  const resetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // 如果有上下文文字，自动生成流程图；否则留空
  useEffect(() => {
    if (show && contextText && contextText.trim()) {
      // 有选中文字，调用 AI 生成
      generateMermaidFromText(contextText);
    } else if (show && initialCode) {
      // 有初始代码（编辑模式）
      setCode(initialCode);
    } else if (show) {
      // 没有选中文字，显示空白编辑器
      setCode('');
    }
  }, [show, contextText, initialCode]);

  // AI 生成 Mermaid 代码
  const generateMermaidFromText = async (text: string) => {
    setIsGenerating(true);
    setError('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/content/generate-mermaid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token') ? { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } : {}),
        },
        body: JSON.stringify({ 
          text,
          selected_model: selectedModel,
          chart_type: 'architecture'  // 默认生成架构图
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: '生成失败' }));
        const errorMessage = errorData.detail || `生成失败: ${response.status}`;
        // Token 欠费：触发全局欠费弹框事件
        if (response.status === 400 && errorMessage.includes('Token') &&
            (errorMessage.includes('欠费') || errorMessage.includes('不足') || errorMessage.includes('额度'))) {
          window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errorMessage } }));
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCode(data.mermaid_code || '');
    } catch (error) {
      console.error('生成 Mermaid 失败:', error);
      const errorMsg = (error as Error)?.message || '';
      // Token 欠费错误不显示 UI 错误提示，会弹出全局欠费弹框
      if (!errorMsg.includes('Token') || (!errorMsg.includes('欠费') && !errorMsg.includes('不足') && !errorMsg.includes('额度'))) {
        setError('AI 生成失败，请手动编辑代码');
      }
      setCode(''); // 失败时也显示空白，让用户手动编辑
    } finally {
      setIsGenerating(false);
    }
  };

  // 默认流程图模板
  const getDefaultMermaid = (): string => {
    return `graph TD
    A[开始] --> B[步骤1]
    B --> C{判断条件}
    C -->|是| D[步骤2]
    C -->|否| E[步骤3]
    D --> F[结束]
    E --> F`;
  };

  // 渲染预览
  useEffect(() => {
    if (code && previewRef.current) {
      renderPreview();
    }
  }, [code]);

  const renderPreview = async () => {
    if (!previewRef.current || !code) return;
    
    try {
      const { svg } = await mermaid.render('mermaid-preview-' + Date.now(), code);
      previewRef.current.innerHTML = svg;
      setError('');
    } catch (err) {
      console.error('Mermaid 渲染错误:', err);
      setError('流程图语法错误，请检查代码');
      previewRef.current.innerHTML = `<div class="text-red-500 text-sm p-4">语法错误: ${err}</div>`;
    }
  };

  const handleInsert = () => {
    if (!code.trim()) {
      setError('请输入流程图代码');
      return;
    }
    onInsert(code);
    handleClose();
  };

  const handleClose = () => {
    setCode('');
    setError('');
    onClose();
  };

  // 加载示例模板
  const loadTemplate = (template: string) => {
    const templates: { [key: string]: string } = {
      architecture: `graph LR
    subgraph 前端层
        A[Web界面]
        B[移动端]
    end
    subgraph 应用层
        C[API网关]
        D[业务服务]
    end
    subgraph 数据层
        E[数据库]
        F[缓存]
    end
    A --> C
    B --> C
    C --> D
    D --> E
    D --> F`,
      flowchart: `graph TD
    A[开始] --> B[步骤1]
    B --> C{判断条件}
    C -->|是| D[步骤2]
    C -->|否| E[步骤3]
    D --> F[结束]
    E --> F`,
      sequence: `sequenceDiagram
    participant A as 用户
    participant B as 系统
    A->>B: 发送请求
    B->>B: 处理请求
    B-->>A: 返回结果`,
      gantt: `gantt
    title 项目进度
    dateFormat  YYYY-MM-DD
    section 阶段1
    需求分析       :a1, 2024-01-01, 7d
    系统设计       :a2, after a1, 5d
    section 阶段2
    开发实现       :a3, after a2, 10d
    测试           :a4, after a3, 5d`,
    };
    setCode(templates[template] || templates.flowchart);
    resetView(); // 切换模板时重置视图
  };

  // 双击放大到200%或恢复100%
  const handleDoubleClick = () => {
    if (scale === 1) {
      setScale(2); // 放大到200%
    } else {
      resetView(); // 恢复默认
    }
  };

  if (!show) return null;

  // 阻止弹框滚轮事件
  const handleDialogWheel = (e: React.WheelEvent) => {
    e.stopPropagation(); // 阻止事件冒泡到下层
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4"
      onWheel={handleDialogWheel} // 在最外层阻止滚轮事件
    >
      {/* 强制覆盖外部CSS污染 - 与样式设置弹框完全一致 */}
      <style>{`
        div.mermaid-dialog-header,
        div.mermaid-dialog-header * {
          line-height: normal !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
        }
        div.mermaid-dialog-header h3 {
          font-size: 16px !important;
          line-height: 1.5 !important;
          color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #mermaid-dialog-desc,
        p#mermaid-dialog-desc,
        div.mermaid-dialog-header p#mermaid-dialog-desc {
          font-size: 10px !important;
          line-height: 1.2 !important;
          color: rgba(191, 219, 254, 1) !important;
          margin: 0 !important;
          margin-top: 2px !important;
          padding: 0 !important;
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[95vh] flex flex-col">
        {/* 头部 - 与样式设置弹框保持一致 */}
        <div className="mermaid-dialog-header bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-white">插入流程图</h3>
              <p id="mermaid-dialog-desc" className="text-[10px] text-blue-100">创建或AI生成Mermaid流程图</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 模板和缩放控制 */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          {/* 模板按钮 */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">模板：</span>
            <button
              onClick={() => loadTemplate('architecture')}
              className="px-3 py-1 text-xs text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors"
            >
              架构图
            </button>
            <button
              onClick={() => loadTemplate('flowchart')}
              className="px-3 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
            >
              流程图
            </button>
            <button
              onClick={() => loadTemplate('sequence')}
              className="px-3 py-1 text-xs text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
            >
              时序图
            </button>
            <button
              onClick={() => loadTemplate('gantt')}
              className="px-3 py-1 text-xs text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
            >
              甘特图
            </button>
          </div>
          
          {/* 缩放控制 */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              🔍 {Math.round(scale * 100)}%
            </span>
            <span className="text-xs text-gray-500">
              💡 滚轮缩放 | 拖动移动
            </span>
            <button
              onClick={resetView}
              className="px-3 py-1 text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
              title="重置视图"
            >
              重置视图
            </button>
          </div>
        </div>

        {/* 左右分屏内容区域 */}
        <div className="flex-1 overflow-hidden flex">
          {isGenerating ? (
            <div className="w-full flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">正在生成流程图...</p>
              </div>
            </div>
          ) : (
            <>
              {/* 左侧：代码编辑器 */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center" style={{ minHeight: '40px' }}>
                  <span className="text-sm font-medium text-gray-700">📝 代码编辑</span>
                </div>
                <div className="flex-1 overflow-hidden" style={{ paddingLeft: '10px' }}>
                  <textarea
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full h-full font-mono text-sm focus:outline-none resize-none"
                    placeholder="输入 Mermaid 代码..."
                    spellCheck={false}
                  />
                </div>
              </div>

              {/* 右侧：实时预览 */}
              <div className="w-1/2 flex flex-col">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between" style={{ minHeight: '40px' }}>
                  <span className="text-sm font-medium text-gray-700">👁️ 实时预览</span>
                  <span className="text-xs text-gray-500">💡 双击快速放大</span>
                </div>
                <div className="flex-1 overflow-hidden" style={{ paddingRight: '10px' }}>
                  <div 
                    ref={previewContainerRef}
                    className="w-full h-full overflow-hidden bg-gray-50 relative"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onDoubleClick={handleDoubleClick}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                  >
                    <div 
                      ref={previewRef} 
                      className="absolute flex items-center justify-center w-full h-full transition-transform duration-100"
                      style={{ 
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transformOrigin: 'center'
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="px-6 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-600">
              {error}
            </div>
          </div>
        )}

        {/* 底部按钮 */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            💡 提示：支持架构图、流程图、时序图、甘特图等多种类型
            <a
              href="https://mermaid.js.org/intro/"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-blue-600 hover:underline"
            >
              查看语法文档
            </a>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleInsert}
              disabled={!code.trim() || isGenerating}
              className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              插入
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MermaidDialog;
